// In src/services/chatService.js

import { db } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';

/**
 * Creates a composite chat ID from two user UIDs.
 * Sorts them alphabetically to ensure the ID is always the same
 * regardless of who initiates the chat.
 * @param {string} uid1 - The first user's UID.
 * @param {string} uid2 - The second user's UID.
 * @returns {string} The composite chat ID.
 */
const createChatId = (uid1, uid2) => {
  return uid1 > uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
};

/**
 * Finds an existing chat between two users or creates a new one.
 * @param {object} currentUserProfile - The profile of the currently logged-in user.
 * @param {object} otherUserProfile - The profile of the user to chat with.
 * @returns {string} The ID of the chat room.
 */
export const createOrGetChat = async (currentUserProfile, otherUserProfile) => {
  if (!currentUserProfile?.authUid || !otherUserProfile?.authUid) {
    throw new Error("User profiles are invalid for creating a chat.");
  }
  
  const chatId = createChatId(currentUserProfile.authUid, otherUserProfile.authUid);
  const chatDocRef = doc(db, 'chats', chatId);

  try {
    const docSnap = await getDoc(chatDocRef);

    // If the chat document doesn't exist, create it.
    if (!docSnap.exists()) {
      await setDoc(chatDocRef, {
        churchId: currentUserProfile.churchId, // Important for scoping and rules
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageText: 'Chat started.',
        memberIds: [currentUserProfile.authUid, otherUserProfile.authUid],
        // Store basic member info to avoid extra lookups when listing chats
        memberInfo: {
          [currentUserProfile.authUid]: {
            firstName: currentUserProfile.firstName,
            lastName: currentUserProfile.lastName,
          },
          [otherUserProfile.authUid]: {
            firstName: otherUserProfile.firstName,
            lastName: otherUserProfile.lastName,
          },
        },
      });
    }

    return chatId; // Return the ID of the existing or new chat
  } catch (error) {
    console.error("Error in createOrGetChat service:", error);
    // Re-throw the error to be caught by the calling function
    throw error;
  }
};


/**
 * Updates the lastRead timestamp for the current user in a specific chat.
 * This is used to clear notifications.
 * @param {string} chatId - The ID of the chat room.
 * @param {string} userId - The UID of the current user.
 */
export const markChatAsRead = async (chatId, userId) => {
  if (!chatId || !userId) return;

  const chatDocRef = doc(db, 'chats', chatId);
  // We use dot notation to update a field within a map (the memberInfo object)
  const userReadField = `memberInfo.${userId}.lastRead`;
  
  try {
    // This sets the user's lastRead time to the current time on the server
    await updateDoc(chatDocRef, {
      [userReadField]: serverTimestamp()
    });
  } catch (error) {
    // This might fail if the document doesn't exist yet, which is okay.
    console.log("Could not mark chat as read (may not exist yet):", error.message);
  }
};
